import { useState, type ReactNode } from 'react';
import { AlertCircle, Check, ChevronsUpDown, FolderKanban, Lock, RefreshCw, Users, User as UserIcon, UsersRound, X } from 'lucide-react';
import type { PrincipalSearchResult, UserRef } from '@vwt/contracts';
import { Avatar, Button, cn, EmptyState, Popover, PopoverContent, PopoverTrigger, Skeleton, Tooltip } from '@vwt/ui';
import { Command } from 'cmdk';
import { Link } from 'react-router';
import { useApiQuery } from '../lib/api.js';
import { errorMessage } from '../lib/errors.js';
import { useTranslation } from '../lib/i18n.js';
import { formatDate, formatDateTime, formatRelative } from '../lib/format.js';

export function PageHeader({ title, subtitle, actions, children }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <div className="border-b border-border bg-surface px-4 pt-4 md:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3 pb-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold tracking-tight text-fg">{title}</h1>
          {subtitle ? <p className="mt-0.5 text-sm text-fg-muted">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}

/** Loading / error / empty states for queries (Section 14: designed states). */
export function QueryState({ query, children, empty, skeleton }: { query: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown }; children: ReactNode; empty?: ReactNode; skeleton?: ReactNode }) {
  const { t } = useTranslation();
  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-2 p-4" aria-busy="true" aria-live="polite">
        {skeleton ?? (
          <>
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-2/3" />
          </>
        )}
      </div>
    );
  }
  if (query.isError) {
    return (
      <EmptyState
        icon={<AlertCircle />}
        title={t('errors.title')}
        description={errorMessage(query.error)}
        action={
          <Button size="sm" onClick={() => void query.refetch()}>
            <RefreshCw /> {t('common.retry')}
          </Button>
        }
      />
    );
  }
  return <>{empty ?? children}</>;
}

/** Work list filtered to the work assigned to one person. */
export function personWorkHref(userId: string): string {
  return `/work?${new URLSearchParams({ assigneeId: userId }).toString()}`;
}

/**
 * A person's avatar and name. With `link`, the chip opens the work list filtered
 * to everything assigned to that person. Only pass `link` where the chip is not
 * already inside another link or button (a nested link is invalid HTML).
 */
export function UserChip({ user, empty, link = false }: { user: UserRef | null | undefined; empty?: string; link?: boolean }) {
  const { t } = useTranslation();
  if (!user) return <span className="text-sm text-fg-subtle">{empty ?? t('common.unassigned')}</span>;
  const content = (
    <>
      <Avatar name={user.displayName} size="sm" />
      <span className="truncate">{user.displayName}</span>
    </>
  );
  if (!link) return <span className="inline-flex min-w-0 items-center gap-1.5 text-sm">{content}</span>;
  return (
    <Link
      to={personWorkHref(user.id)}
      title={t('common.viewPersonWork', { name: user.displayName })}
      // Table rows open their item on click / Enter: the link must not trigger that too.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className="inline-flex min-w-0 items-center gap-1.5 rounded-sm text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {content}
    </Link>
  );
}

export function TimeAgo({ iso }: { iso: string | null | undefined }) {
  if (!iso) return null;
  return (
    <time dateTime={iso} title={formatDateTime(iso)} className="whitespace-nowrap text-fg-subtle">
      {formatRelative(iso)}
    </time>
  );
}

/**
 * A calendar date with the full date and time in a tooltip (imported work keeps its original creation time).
 * Inside a link (`inLink`) it shows the full date as a plain title: a focusable element there is invalid HTML.
 */
export function DateWithTime({ iso, inLink = false }: { iso: string | null | undefined; inLink?: boolean }) {
  if (!iso) return null;
  const full = formatDateTime(iso, { dateStyle: 'full', timeStyle: 'short' });
  if (inLink) {
    return (
      <time dateTime={iso} title={full} className="whitespace-nowrap text-fg-muted">
        {formatDate(iso)}
      </time>
    );
  }
  return (
    <Tooltip content={full}>
      <time dateTime={iso} tabIndex={0} className="whitespace-nowrap text-fg-muted">
        {formatDate(iso)}
      </time>
    </Tooltip>
  );
}

const principalIcon = { USER: UserIcon, TEAM: Users, GROUP: UsersRound, PROJECT: FolderKanban } as const;

/** Searchable person/team/group picker backed by the bounded principal search. */
export function PrincipalPicker({
  value,
  onChange,
  kinds = 'USER',
  placeholder,
  allowClear = true,
  label,
  disabled,
  open: controlledOpen,
  onOpenChange,
}: {
  value: { id: string; name: string; kind?: string } | null;
  onChange: (value: PrincipalSearchResult | null) => void;
  kinds?: string;
  placeholder?: string;
  allowClear?: boolean;
  label: string;
  disabled?: boolean;
  /** Optional control, for example to open the picker from a keyboard shortcut. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const setOpen = (next: boolean) => {
    setLocalOpen(next);
    onOpenChange?.(next);
  };
  const [q, setQ] = useState('');
  const results = useApiQuery('searchPrincipals', { query: { q, kinds } }, { enabled: open });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          disabled={disabled}
          className="flex h-8 w-full items-center gap-2 rounded-[var(--radius)] border border-border-strong bg-surface px-2.5 text-left text-sm shadow-xs hover:bg-surface-2 disabled:opacity-60"
        >
          {value ? (
            <>
              <Avatar name={value.name} size="sm" />
              <span className="truncate">{value.name}</span>
            </>
          ) : (
            <span className="truncate whitespace-nowrap text-fg-subtle">{placeholder ?? t('common.unassigned')}</span>
          )}
          <ChevronsUpDown className="ml-auto size-3.5 text-fg-subtle" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command shouldFilter={false} label={label}>
          <Command.Input value={q} onValueChange={setQ} autoFocus placeholder={t('visibility.searchPrincipal')} className="h-9 w-full border-b border-border bg-transparent px-3 text-sm outline-none" />
          <Command.List className="max-h-64 overflow-y-auto p-1">
            <Command.Empty className="px-2 py-4 text-center text-xs text-fg-subtle">{t('common.noResults')}</Command.Empty>
            {allowClear && value ? (
              <Command.Item
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg-muted data-[selected=true]:bg-muted"
              >
                <X className="size-4" /> {placeholder ?? t('common.unassigned')}
              </Command.Item>
            ) : null}
            {(results.data?.data ?? []).map((p) => {
              const Icon = principalIcon[p.kind];
              return (
                <Command.Item
                  key={`${p.kind}:${p.id}`}
                  value={`${p.kind}:${p.id}`}
                  onSelect={() => {
                    onChange(p);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm data-[selected=true]:bg-muted"
                >
                  {p.kind === 'USER' ? <Avatar name={p.name} size="sm" /> : <Icon className="size-4 text-fg-subtle" />}
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  {p.detail ? <span className="truncate text-xs text-fg-subtle">{p.detail}</span> : null}
                  {value?.id === p.id ? <Check className="size-4 text-accent" /> : null}
                </Command.Item>
              );
            })}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function SectionTitle({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h3 className="text-xs font-semibold tracking-wide text-fg-subtle uppercase">{children}</h3>
      {actions}
    </div>
  );
}

export function PropertyRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[92px_minmax(0,1fr)] items-center gap-2 py-1.5 text-sm">
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

export function RestrictedHint({ children }: { children: ReactNode }) {
  return (
    <p className={cn('flex items-center gap-1.5 text-xs text-fg-subtle')}>
      <Lock className="size-3" /> {children}
    </p>
  );
}
